import { fetchJsonDynamic } from "../../data/DynamicServiceNew/jsondynamicservice";
import { userManagementUrl, createUsersClient } from "../../config";
import { GET_USERS } from "../../data/userData";

// Module-level cache so users are loaded once across all admin schedule tabs
let cachedUserMap = null;
let fetchPromise = null;

function populateFromToken(map) {
  try {
    const raw = localStorage.getItem("decodedToken");
    if (raw) {
      const d = JSON.parse(raw);
      const uid = Number(d?.Id ?? d?.id ?? d?.UserId ?? d?.userId);
      const first = (d?.FirstName ?? d?.firstName ?? "").trim();
      const last = (d?.LastName ?? d?.lastName ?? "").trim();
      const full = `${first} ${last}`.trim();
      const name = full || d?.Username || d?.username || d?.Name || d?.name || d?.unique_name;
      if (uid && name) {
        map.set(uid, name);
      }
    }
  } catch (e) {
    // Ignore JSON parse errors
  }
}

function parseUserList(list, map) {
  if (!Array.isArray(list)) return;
  list.forEach((u) => {
    const id = Number(u.Id ?? u.id);
    if (!id) return;
    const first = (u.FirstName ?? u.firstName ?? "").trim();
    const last = (u.LastName ?? u.lastName ?? "").trim();
    const full = `${first} ${last}`.trim();
    const name = full || (u.Name ?? u.name ?? "").trim() || (u.Username ?? u.username ?? "").trim();
    if (name) {
      map.set(id, name);
    }
  });
}

/**
 * Loads all users from the user management service and returns a Map<userId, displayName>.
 * Deduped with in-flight Promise and cached in memory.
 */
export async function loadUsersMap() {
  if (cachedUserMap && cachedUserMap.size > 0) return cachedUserMap;
  if (fetchPromise) return fetchPromise;

  fetchPromise = (async () => {
    const map = new Map();
    populateFromToken(map);

    // 1. Try fetchJsonDynamic with allUsersView
    try {
      if (userManagementUrl?.uri) {
        const res = await fetchJsonDynamic("allUsersView", null, null, userManagementUrl.uri);
        const raw = res?.results;
        const list = Array.isArray(raw)
          ? raw
          : typeof raw === "string"
          ? JSON.parse(raw)
          : [];
        parseUserList(list, map);
      }
    } catch (err) {
      console.warn("[userResolver] fetchJsonDynamic error:", err);
    }

    // 2. If map still empty (or only token user), try createUsersClient with GET_USERS
    if (map.size <= 1) {
      try {
        const client = createUsersClient();
        const { data } = await client.query({ query: GET_USERS });
        const raw = data?.allUsersView?.results;
        const list = Array.isArray(raw)
          ? raw
          : typeof raw === "string"
          ? JSON.parse(raw)
          : [];
        parseUserList(list, map);
      } catch (err) {
        console.warn("[userResolver] Apollo query error:", err);
      }
    }

    if (map.size > 1) {
      cachedUserMap = map;
    }
    return map;
  })();

  return fetchPromise;
}

let cachedActiveUsersList = null;
let activeUsersPromise = null;

/**
 * Loads all active users from the user management service and returns Array<{ id, name, username, email }>.
 * Filtered by IsActive !== false and sorted by highest ID first.
 */
export async function loadActiveUsersList() {
  if (cachedActiveUsersList && cachedActiveUsersList.length > 0) return cachedActiveUsersList;
  if (activeUsersPromise) return activeUsersPromise;

  activeUsersPromise = (async () => {
    let rawList = [];
    try {
      if (userManagementUrl?.uri) {
        const res = await fetchJsonDynamic("allUsersView", null, null, userManagementUrl.uri);
        const raw = res?.results;
        rawList = Array.isArray(raw)
          ? raw
          : typeof raw === "string"
          ? JSON.parse(raw)
          : [];
      }
    } catch (err) {
      console.warn("[userResolver] fetchJsonDynamic error:", err);
    }

    if (!rawList.length) {
      try {
        const client = createUsersClient();
        const { data } = await client.query({ query: GET_USERS });
        const raw = data?.allUsersView?.results;
        rawList = Array.isArray(raw)
          ? raw
          : typeof raw === "string"
          ? JSON.parse(raw)
          : [];
      } catch (err) {
        console.warn("[userResolver] Apollo query error:", err);
      }
    }

    const active = rawList
      .filter((u) => u?.IsActive !== false && u?.isActive !== false)
      .map((u) => {
        const id = Number(u.Id ?? u.id);
        const first = (u.FirstName ?? u.firstName ?? "").trim();
        const last = (u.LastName ?? u.lastName ?? "").trim();
        const full = `${first} ${last}`.trim();
        const name = full || (u.Name ?? u.name ?? "").trim() || (u.Username ?? u.username ?? "").trim() || `User #${id}`;
        return {
          id,
          name,
          username: u.Username ?? u.username,
          email: u.Email ?? u.email,
          isActive: true,
        };
      })
      .filter((u) => Boolean(u.id))
      .sort((a, b) => (b.id || 0) - (a.id || 0));

    cachedActiveUsersList = active;
    return active;
  })();

  return activeUsersPromise;
}

/**
 * Returns the resolved display name for a given user ID, or falls back to "User #<id>".
 */
export function getUserDisplayName(userId, userMap) {
  if (!userId && userId !== 0) return "—";
  const numId = Number(userId);
  if (userMap instanceof Map && userMap.has(numId)) {
    return userMap.get(numId);
  }
  if (cachedUserMap && cachedUserMap.has(numId)) {
    return cachedUserMap.get(numId);
  }
  return `User #${userId}`;
}

/**
 * Given a list of user IDs not resolved by loadUsersMap, fetches each one
 * individually from the user management service and patches the module cache.
 * Returns an updated Map with newly resolved names merged in.
 */
export async function resolveUnknownUsers(unknownIds) {
  if (!unknownIds || unknownIds.length === 0) return cachedUserMap || new Map();

  const map = cachedUserMap ? new Map(cachedUserMap) : new Map();
  const missing = unknownIds.map(Number).filter((id) => id > 0 && !map.has(id));

  if (missing.length === 0) return map;

  await Promise.allSettled(
    missing.map(async (id) => {
      try {
        if (!userManagementUrl?.uri) return;
        const res = await fetchJsonDynamic(
          "allUsersView",
          "usermodel",
          { Id: id },
          userManagementUrl.uri
        );
        const raw = res?.results;
        const list = Array.isArray(raw)
          ? raw
          : typeof raw === "string"
          ? (() => { try { return JSON.parse(raw); } catch { return []; } })()
          : [];
        if (list.length > 0) {
          const u = list.find((x) => Number(x.Id ?? x.id) === id) || (list.length === 1 && Number(list[0]?.Id ?? list[0]?.id) === id ? list[0] : null);
          if (!u) return;
          const first = (u.FirstName ?? u.firstName ?? "").trim();
          const last = (u.LastName ?? u.lastName ?? "").trim();
          const full = `${first} ${last}`.trim();
          const name =
            full ||
            (u.Name ?? u.name ?? "").trim() ||
            (u.Username ?? u.username ?? "").trim();
          if (name) map.set(id, name);
        }
      } catch {
        // silently ignore individual fetch failures
      }
    })
  );

  if (cachedUserMap) {
    map.forEach((name, id) => cachedUserMap.set(id, name));
  } else {
    cachedUserMap = map;
  }

  return map;
}

/**
 * Extracts the logged-in user's email from decodedToken or JWT token in localStorage
 * and returns it masked (e.g. "jo***n@domain.com") for privacy in OTP prompt dialogs.
 */
export function getMaskedUserEmail() {
  try {
    let email = null;
    const raw = localStorage.getItem("decodedToken");
    if (raw) {
      const d = JSON.parse(raw);
      email =
        d?.Email ||
        d?.email ||
        d?.["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress"];
    }
    if (!email) {
      const token = localStorage.getItem("token");
      if (token) {
        try {
          const parts = token.replace(/"/g, "").split(".");
          if (parts.length === 3) {
            const payload = JSON.parse(atob(parts[1]));
            email =
              payload?.Email ||
              payload?.email ||
              payload?.["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress"];
          }
        } catch {}
      }
    }
    if (email && typeof email === "string" && email.includes("@")) {
      const [name, domain] = email.trim().split("@");
      if (name.length <= 2) {
        return `${name[0]}*@${domain}`;
      }
      const visible = Math.min(2, Math.max(1, Math.floor(name.length / 3)));
      const maskedLength = Math.max(name.length - visible - 1, 3);
      const masked = "*".repeat(maskedLength);
      const last = name[name.length - 1];
      return `${name.slice(0, visible)}${masked}${last}@${domain}`;
    }
  } catch (e) {
    console.warn("Could not parse user email:", e);
  }
  return null;
}
